/*
TypeScript version of WatchJS (simplified for modern browsers)
Author: Gil Lopes Bueno (original), rewritten by GitHub Copilot
GitHub: https://github.com/melanke/Watch.JS
*/

import { warn } from 'cc';

export type Watcher<T extends IWatchable = {}, TKey extends keyof T = keyof T> = (prop: TKey, newval: T[TKey], oldval: T[TKey]) => void;

interface IWatchable extends Object {
    watchers?: Record<string, Watcher[]>;
}

function isFunction(fn: any): fn is Function {
    return typeof fn === 'function';
}

function defineGetAndSet<T, TKey extends keyof T>(obj: T, propName: TKey, getter: () => T[TKey], setter: (val: T[TKey]) => void): boolean {
    const originalDescriptor = Object.getOwnPropertyDescriptor(obj, propName);
    if (originalDescriptor && originalDescriptor.configurable === false) {
        warn(`Cannot redefine property: ${String(propName)}`);
        return false;
    }
    Object.defineProperty(obj, propName, {
        get: getter,
        set: setter,
        enumerable: true,
        configurable: true,
    });
    return true;
}

function defineProp<T, TKey extends keyof T>(obj: T, propName: TKey, value: T[TKey]): boolean {
    const originalDescriptor = Object.getOwnPropertyDescriptor(obj, propName);
    if (originalDescriptor && originalDescriptor.configurable === false) {
        warn(`Cannot redefine property: ${String(propName)}`);
        return false;
    }
    Object.defineProperty(obj, propName, {
        enumerable: false,
        configurable: true,
        writable: false,
        value,
    });
    return true;
}

function defineWatcher<T extends IWatchable, TKey extends keyof T>(obj: T, prop: TKey, watcher: Watcher<T, TKey>) {
    let val = obj[prop];
    const getter = () => val;
    const setter = (newval: T[TKey]) => {
        const oldval = val;
        val = newval;
        if (oldval !== newval) {
            callWatchers(obj, prop, newval, oldval);
        }
    };
    if (!defineGetAndSet(obj, prop, getter, setter)) {
        warn(`Failed to define watcher for property: ${String(prop)}`);
    } else {
        if (!obj.watchers) {
            defineProp(obj, 'watchers', {});
        }
        const propStr = String(prop);
        if (!obj.watchers![propStr]) {
            obj.watchers![propStr] = [];
        }
        obj.watchers![propStr].push(watcher as Watcher);
    }
}

function callWatchers<T extends IWatchable, TKey extends keyof T>(obj: T, prop: TKey, newval: T[TKey], oldval?: T[TKey]) {
    const propStr = String(prop);
    if (!obj.watchers || !obj.watchers[propStr]) {
        return;
    }
    obj.watchers[propStr].forEach((watcher) => {
        (watcher as Watcher<T, TKey>).call(obj, prop, newval, oldval as T[TKey]);
    });
}

function watchAll<T extends IWatchable>(obj: T, watcher: Watcher<T, keyof T>, level?: number) {
    if (typeof obj !== 'object' || obj === null) {
        return;
    }
    const props = (Array.isArray(obj) ? Object.keys(obj) : Object.getOwnPropertyNames(obj)) as (keyof T)[];
    props.forEach((prop) => {
        watchOne(obj, prop, watcher, level);
    });
}

function watchMany<T extends IWatchable, TKey extends keyof T>(obj: T, props: TKey[], watcher: Watcher<T, TKey>, level?: number) {
    props.forEach((prop) => {
        watchOne(obj, prop, watcher, level);
    });
}

function watchOne<T extends IWatchable, TKey extends keyof T>(obj: T, prop: TKey, watcher: Watcher<T, TKey>, level?: number) {
    if (isFunction(obj[prop])) {
        return;
    }
    if (obj[prop] != null && (level === undefined || level > 0)) {
        if (level !== undefined) {
            level--;
        }
        watchAll(obj[prop], watcher as Watcher<any, any>, level);
    }
    defineWatcher(obj, prop, watcher);
}

// Overloads for watch
function watch<T extends IWatchable>(obj: T, watcher: Watcher<T, keyof T>, level?: number): void;
function watch<T extends IWatchable, TKey extends keyof T>(obj: T, props: TKey[], watcher: Watcher<T, TKey>, level?: number): void;
function watch<T extends IWatchable, TKey extends keyof T>(obj: T, prop: TKey, watcher: Watcher<T, TKey>, level?: number): void;
function watch<T extends IWatchable, TKey extends keyof T>(
    obj: T,
    propOrWatcher: TKey | TKey[] | Watcher<T, TKey>,
    watcherOrLevel?: Watcher<T, TKey> | number,
    level?: number,
): void {
    if (isFunction(propOrWatcher)) {
        watchAll(obj, propOrWatcher as Watcher<T, keyof T>, watcherOrLevel as number | undefined);
    } else if (Array.isArray(propOrWatcher)) {
        watchMany(obj, propOrWatcher as TKey[], watcherOrLevel as Watcher<T, TKey>, level);
    } else {
        watchOne(obj, propOrWatcher as TKey, watcherOrLevel as Watcher<T, TKey>, level);
    }
}

// Overloads for unwatch
function unwatch<T extends IWatchable>(obj: T, watcher: Watcher<T, keyof T>): void;
function unwatch<T extends IWatchable, TKey extends keyof T>(obj: T, props: TKey[], watcher: Watcher<T, TKey>): void;
function unwatch<T extends IWatchable, TKey extends keyof T>(obj: T, prop: TKey, watcher: Watcher<T, TKey>): void;
function unwatch<T extends IWatchable, TKey extends keyof T>(obj: T, propOrWatcher: TKey | TKey[] | Watcher<T, TKey>, watcher?: Watcher<T, TKey>): void {
    if (!obj.watchers) {
        return;
    }
    if (isFunction(propOrWatcher)) {
        Object.keys(obj.watchers).forEach((prop) => {
            obj.watchers![prop] = obj.watchers![prop].filter((w) => w !== propOrWatcher);
        });
    } else if (Array.isArray(propOrWatcher)) {
        (propOrWatcher as TKey[]).forEach((prop) => {
            const key = String(prop);
            if (obj.watchers![key]) {
                obj.watchers![key] = obj.watchers![key].filter((w) => w !== watcher);
            }
        });
    } else {
        const key = String(propOrWatcher);
        if (obj.watchers![key]) {
            obj.watchers![key] = obj.watchers![key].filter((w) => w !== watcher);
        }
    }
}

defineProp(window as any, 'watch', watch);
defineProp(window as any, 'unwatch', unwatch);

export { watch, unwatch };
